<template>
  <!-- Audit plan import, step 4: switch objectives and controls on or off and correct them before saving (US 9404) -->
  <div class="api-review" qid="audit-plan-review">
    <div class="api-review__tree">
      <div class="api-review__bar">
        <div class="at-filter api-review__search">
          <font-awesome-icon icon="search"/>
          <input v-model="search" :placeholder="$t('audit_plan_import.review.search')" :aria-label="$t('audit_plan_import.review.search')" autocomplete="off" qid="audit-plan-review-search">
        </div>
      </div>

      <div v-for="objective in shown"
           :key="objective.key"
           class="api-obj"
           :class="{ 'is-off': objective.off }"
           qid="audit-plan-review-objective"
      >
        <div class="api-obj__head" :class="{ 'is-selected': selected === objective.key }">
          <button type="button"
                  class="us-toggle us-toggle--teal"
                  :class="{ 'is-on': !objective.off }"
                  role="switch"
                  :aria-checked="objective.off ? 'false' : 'true'"
                  :aria-label="$t('audit_plan_import.review.include')"
                  @click="$emit('toggle', objective.key)"
          ></button>
          <button type="button" class="api-obj__open" :aria-expanded="isOpen(objective) ? 'true' : 'false'" @click="toggleOpen(objective)">
            <font-awesome-icon :icon="isOpen(objective) ? 'chevron-down' : 'chevron-right'"/>
          </button>
          <button type="button" class="api-obj__text" @click="$emit('select', objective.key)">
            <b><span v-if="objective.reference" class="api-ref api-ref--violet">{{ objective.reference }}</span>{{ objective.name || $t('audit_plan_import.review.no_name') }}</b>
            <small v-if="objective.description">{{ objective.description }}</small>
          </button>
          <span v-if="problems[objective.key]" class="v2-tag v2-tag--red">{{ $t('audit_plan_import.review.problem_no_name') }}</span>
          <span class="us-muted api-obj__count">{{ $t('audit_plan_import.review.count', { on: onCount(objective), total: objective.controls.length }) }}</span>
        </div>

        <template v-if="isOpen(objective)">
          <div v-for="control in objective.visibleControls"
               :key="control.key"
               class="api-control"
               :class="{ 'is-off': control.off || objective.off, 'is-selected': selected === control.key }"
               qid="audit-plan-review-control"
          >
            <button type="button"
                    class="us-toggle us-toggle--teal"
                    :class="{ 'is-on': !control.off }"
                    role="switch"
                    :disabled="objective.off"
                    :aria-checked="control.off ? 'false' : 'true'"
                    :aria-label="$t('audit_plan_import.review.include')"
                    @click="$emit('toggle', control.key)"
            ></button>
            <button type="button" class="api-control__text" @click="$emit('select', control.key)">
              <span class="api-ref">{{ control.reference || '–' }}</span>
              <span>
                <b>{{ control.name || $t('audit_plan_import.review.no_name') }}</b>
                <small>{{ control.description }}</small>
              </span>
            </button>
            <span class="api-control__flags">
              <span v-for="problem in problems[control.key] || []" :key="problem" class="v2-tag v2-tag--red">{{ $t('audit_plan_import.review.problem_' + problem) }}</span>
              <span v-for="flag in control.flags" :key="flag" class="v2-tag" :class="flagClass(flag)">{{ $t('audit_plan_import.review.flag_' + flag) }}</span>
            </span>
          </div>
        </template>
      </div>
      <div v-if="!shown.length" class="ta-empty">{{ $t('audit_plan_import.review.nothing') }}</div>
    </div>

    <!-- The selected objective or control -->
    <div class="api-review__detail" qid="audit-plan-review-detail">
      <template v-if="selectedItem">
        <div class="api-review__detail-head">
          <b>{{ selectedItem.reference || (selectedIsObjective ? $t('audit_plan_import.review.objective') : $t('audit_plan_import.review.control')) }}</b>
          <span v-if="selectedItem.page" class="v2-tag v2-tag--violet">{{ $t('audit_plan_import.review.page', { page: selectedItem.page }) }}</span>
        </div>
        <div v-for="field in detailFields" :key="field" class="api-field">
          <label :for="'api-field-' + field">
            {{ fieldLabel(field) }}<span v-if="required(field)" class="mandatory"> *</span>
            <span v-if="field === 'name' && aiName" class="v2-tag v2-tag--violet">{{ $t('audit_plan_import.review.ai_title') }}</span>
          </label>
          <b-form-input v-if="field === 'name' || field === 'reference'"
                        :id="'api-field-' + field"
                        :value="selectedItem[field]"
                        :maxLength="255"
                        :state="fieldState(field)"
                        @input="edit(field, $event)"
          ></b-form-input>
          <b-form-textarea v-else
                           :id="'api-field-' + field"
                           :value="selectedItem[field]"
                           rows="3"
                           max-rows="10"
                           :state="fieldState(field)"
                           @input="edit(field, $event)"
          ></b-form-textarea>
        </div>
        <div v-if="!selectedIsObjective" class="us-note us-note--info api-review__defaults">
          <font-awesome-icon icon="info-circle"/>
          <span>{{ $t('audit_plan_import.review.defaults') }}</span>
        </div>
      </template>
      <span v-else class="us-muted">{{ $t('audit_plan_import.review.select') }}</span>
    </div>
  </div>
</template>

<script>
  import { CONTROL_FIELDS } from './auditPlanMapping.js';

  const FLAG_CLASSES = {
    joined: 'v2-tag--blue',
    not_a_control: 'v2-tag--red',
    column_mixup: 'v2-tag--amber',
    uncertain: 'v2-tag--amber',
    no_objective: 'v2-tag--amber',
    reference_numbered: 'v2-tag--blue',
    reference_changed: 'v2-tag--blue'
  };

  export default {
    name: 'ImportReview',
    props: {
      // Mapped objectives with the user's edits, each with off and controls
      objectives: {
        type: Array,
        required: true
      },
      // Problems that stop saving, by key
      problems: {
        type: Object,
        required: true
      },
      // Fields that have a mapped column, shown in the edit panel
      fields: {
        type: Array,
        required: true
      },
      selected: {
        type: String,
        default: null
      },
      // Whether control names are written by the AI
      aiName: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        search: '',
        closed: {}
      };
    },
    computed: {
      shown() {
        let search = this.search.trim().toLowerCase();
        return this.objectives.map(objective => {
          let visibleControls = objective.controls.filter(control => {
            return !search || [control.reference, control.name, control.description].join(' ').toLowerCase().indexOf(search) !== -1;
          });
          return Object.assign({}, objective, { visibleControls });
        }).filter(objective => objective.visibleControls.length || !search);
      },
      selectedItem() {
        for (let objective of this.objectives) {
          if (objective.key === this.selected) {
            return objective;
          }
          let control = objective.controls.find(control => control.key === this.selected);
          if (control) {
            return control;
          }
        }
        return null;
      },
      selectedIsObjective() {
        return !!this.objectives.find(objective => objective.key === this.selected);
      },
      detailFields() {
        if (this.selectedIsObjective) {
          return ['name', 'description'];
        }
        // Always name, reference and description; other fields when a column goes there or the control has text
        return CONTROL_FIELDS.filter(field => ['name', 'reference', 'description'].indexOf(field) !== -1
          || this.fields.indexOf(field) !== -1 || (this.selectedItem && this.selectedItem[field]));
      }
    },
    methods: {
      onCount(objective) {
        return objective.off ? 0 : objective.controls.filter(control => !control.off).length;
      },
      isOpen(objective) {
        return !this.closed[objective.key] || !!this.search;
      },
      toggleOpen(objective) {
        this.$set(this.closed, objective.key, !this.closed[objective.key]);
      },
      flagClass(flag) {
        return FLAG_CLASSES[flag] || 'v2-tag--grey';
      },
      required(field) {
        return field === 'name' || (!this.selectedIsObjective && (field === 'description' || field === 'reference'));
      },
      fieldLabel(field) {
        if (this.selectedIsObjective) {
          return this.$t('audit_plan_import.mapping.objective_' + field);
        }
        return this.$t('audit_plan_import.fields.' + field);
      },
      fieldState(field) {
        return this.required(field) && !(this.selectedItem[field] || '').trim() ? false : null;
      },
      edit(field, value) {
        this.$emit('edit', this.selected, { [field]: value });
      }
    }
  }
</script>
